import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { SemnaturaPad } from '../../components/SemnaturaPad'

/** Cat primeste serverul la „Observatii” (`PredareBody`). */
const MAX_OBSERVATII = 1000

/**
 * Formularul de la codul QR. Singura pagină din CRM deschisă fără autentificare.
 *
 * Se folosește `axios` simplu, nu clientul din `lib/api`: acela atașează token, iar la 401
 * încearcă refresh și golește sesiunea. Un vizitator care n-a fost niciodată logat n-are ce
 * căuta în mecanismul ăla, iar un angajat logat pe telefon nu trebuie deconectat fiindcă a
 * deschis un link public.
 *
 * Ecranul e gândit pentru telefonul primitorului, ținut în mână, la un stand: un singur
 * fir vertical, câmpuri mari, fără nimic de derulat lateral.
 */

interface Produs {
  /** Id-ul din catalog. Vine doar la produsele de cadou — acolo se alege unul. */
  productId?: number
  denumire: string
  cantitate: number
  um: string
}

interface Actiune {
  denumire: string
  /** Textul de deasupra formularului. `null` = implicitul tipului de actiune. */
  textIntro: string | null
  tip: string
  locatie: string | null
  produse: Produs[]
  functii: string[]
  /** Cadoul oferit la tombolă: ce se dă și în ce condiție. `null` = nu se oferă nimic. */
  cadou: { produse: Produs[]; conditie: string | null } | null
  textInformare: string | null
  versiuneInformare: string | null
  textRegulament: string | null
  areAfis: boolean
  afisVersiune: number
  urlPolitica: string | null
  urlRegulament: string | null
  textMarketing: string | null
  versiuneMarketing: string | null
  /** Se predă chiar acum, la semnătură: formularul cere și semnătura, iar omul pleacă cu produsele. */
  predareLaSemnatura: boolean
}

/** Tombola nu predă nimic: fără produse, fără semnătură, fără proces-verbal. */
function eTombola(a: Actiune | null): boolean {
  return a?.tip === 'tombola'
}

export function SamplingFormPage() {
  const { slug = '' } = useParams()
  const [params] = useSearchParams()
  const agent = params.get('a')

  const [actiune, setActiune] = useState<Actiune | null>(null)
  const [eroareIncarcare, setEroareIncarcare] = useState<string | null>(null)

  const [nume, setNume] = useState('')
  const [prenume, setPrenume] = useState('')
  const [email, setEmail] = useState('')
  const [telefon, setTelefon] = useState('')
  const [functie, setFunctie] = useState('')
  const [observatii, setObservatii] = useState('')
  const [acordInformare, setAcordInformare] = useState(false)
  /* Doar la actiunile care predau pe loc. Vezi `predareLaSemnatura`. */
  const [semnatura, setSemnatura] = useState<string | null>(null)
  const [acceptRegulament, setAcceptRegulament] = useState(false)
  /** Declaratia de 18 ani. Regulamentul o cere la art. 4.1 si 5.1. */
  const [confirmare18, setConfirmare18] = useState(false)
  /*
    Stocul cadoului. Regulamentul cere ca epuizarea sa fie anuntata „si in Formular cat mai curand
    posibil" (art. 9.6), iar mentiunea „in limita stocului disponibil" sa fie vizibila (art. 9.5).
    `null` = inca nu stim; nu se afirma nimic pana nu raspunde serverul.
  */
  const [stocEpuizat, setStocEpuizat] = useState<boolean | null>(null)
  const [acordEmail, setAcordEmail] = useState(false)
  const [acordSms, setAcordSms] = useState(false)
  /*
    Formularul public FACE DOAR INSCRIEREA. Doi pasi: datele, apoi confirmarea.

    Avea patru. Ultimii doi — validarea conditiei, alegerea produsului, semnatura, procesul-verbal
    — pretindeau ca predarea se poate face de pe telefonul participantului, cu operatorul aplecat
    peste umarul lui sa bifeze „conditia e indeplinita". Alexandru, 26.08: „as vrea ca
    participantul sa confirme prin semnare de pe telefonul consultantului (...) pasii 3 si 4 cred
    ca trebuie eliminati de aici".

    Predarea traieste acum intr-un singur loc: vederea de stand, pe telefonul consultantului.
    Acolo el cauta omul, verifica Story-ul, alege produsul, iar participantul semneaza. Un singur
    drum, cu o singura verificare, facuta de cine trebuie.
  */
  const [pas, setPas] = useState<'date' | 'salvata' | 'gata'>('date')
  /*
    Tokenul primit la inscriere. Autorizeaza UN singur lucru: „vreau produsul gratuit", la pasul
    2. Predarea nu mai trece pe aici — se face la stand, pe telefonul consultantului.
  */
  const [cadouToken, setCadouToken] = useState<string | null>(null)
  /** A cerut produsul la pasul 2. Schimba doar ce scrie pe ecranul final. */
  const [vreaCadou, setVreaCadou] = useState(false)

  const [website, setWebsite] = useState('') // capcană pentru roboți

  const [seTrimite, setSeTrimite] = useState(false)
  const [eroare, setEroare] = useState<string | null>(null)
  const [nrPv, setNrPv] = useState<string | null>(null)

  useEffect(() => {
    let anulat = false
    axios
      .get<Actiune>(`/api/public/sampling/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (!anulat) setActiune(r.data)
      })
      .catch((err) => {
        if (anulat) return
        setEroareIncarcare(
          mesajEroare(
            err,
            'Această campanie nu mai este activă. Dacă ai scanat codul la stand, adresează-te echipei Boost4Life.',
          ),
        )
      })
    return () => {
      anulat = true
    }
  }, [slug])

  /*
    Stocul cadoului, citit o singura data la deschiderea formularului.

    Regulamentul cere ca epuizarea sa se vada „si in Formular cat mai curand posibil" (art. 9.6).
    Daca cererea esueaza, nu se afirma nimic: `stocEpuizat` ramane `null` si ecranul spune doar
    „in limita stocului disponibil", ca inainte.
  */
  useEffect(() => {
    if (!actiune?.cadou) return
    let anulat = false
    axios
      .get<{ produse: { productId: number; stoc: number | null }[] }>(
        `/api/public/sampling/${encodeURIComponent(slug)}/cadou-stoc`,
      )
      .then((r) => {
        if (anulat) return
        const cunoscute = r.data.produse.filter((p) => p.stoc != null)
        if (cunoscute.length === 0) return
        setStocEpuizat(cunoscute.every((p) => (p.stoc ?? 0) <= 0))
      })
      .catch(() => {
        // Necunoscut ramane necunoscut. Nu speriem pe nimeni cu o eroare de retea.
      })
    return () => {
      anulat = true
    }
  }, [actiune?.cadou, slug])

  const tombola = eTombola(actiune)
  /*
    Se predă ceva? Se citește din produse, nu din tipul acțiunii: o tombolă cu produse predă la
    fel ca o farmacie — semnătură, proces-verbal, scădere din gestiune. Una fără produse rămâne
    doar înscriere. Serverul judecă la fel; ecranul nu are voie să întrebe altceva decât acceptă
    el, altfel omul semnează degeaba sau nu i se cere semnătura când trebuie.
  */
  /*
    Se predă ceva CHIAR ACUM, în formular?

    La farmacie și la evenimente, da: omul primește mostrele și semnează pe loc. La tombolă, NU —
    nici când acțiunea are un produs. Acolo produsele sunt cadoul, iar cadoul se ia la pasul 3,
    după ce operatorul confirmă condiția.

    Fără distincția asta, formularul cerea semnătură ca să te înscrii la tombolă și ținea butonul
    stins până semnai — pentru un document care nici nu se emitea. Serverul trata deja tombola ca
    acțiune fără predare, deci semnătura se ducea la coș.
  */
  /*
    Formularul NU predă nimic. Niciodată, la niciun tip de acțiune.

    Până pe 24.09.2026 exista și calea cealaltă: cine completa formularul la o acțiune de mostre
    semna aici și pleca automat cu TOT ce era configurat pe acțiune. Două căi pentru aceeași
    predare înseamnă două adevăruri despre ea — și chiar s-au contrazis: bifa „fără mostre" scria
    pe ecran „nu se predă nimic", în timp ce predarea de la stand mergea mai departe.

    Alexandru: „vreau ca predarea de produse să se facă DOAR din Predare la stand." Deci de aici
    au plecat chenarul de semnătură, listele „Primești" / „Ai primit" și promisiunea
    procesului-verbal. Omul se înscrie; ce primește decide agentul, în fața lui.
  */
  /*
    Cadoul de la tombolă. Se cere din formular, dar la pasul următor — nu aici.
  */
  const cadou = actiune?.cadou ?? null

  /*
    Actiunea preda CHIAR ACUM, la semnatura — evenimentele mici, unde datele se colecteaza si
    produsele se dau in aceeasi clipa. Atunci formularul cere si semnatura, iar ce scrie deasupra
    ei se schimba: omul semneaza si de primire.
  */
  const predaAcum = actiune?.predareLaSemnatura === true

  const gata =
    nume.trim().length >= 2 &&
    prenume.trim().length >= 2 &&
    /.+@.+\..+/.test(email.trim()) &&
    acordInformare &&
    // Regulamentul se cere doar unde exista. Serverul verifica la fel; ecranul nu are voie sa
    // ceara altceva decat accepta el.
    (!actiune?.textRegulament || acceptRegulament) &&
    // Varsta, la tombola. Serverul verifica la fel.
    (!tombola || confirmare18) &&
    // La tombolă telefonul e obligatoriu: pe el se contactează câștigătorul.
    (!tombola || telefon.trim().length >= 9) &&
    // La acțiunile care predau pe loc, semnătura e chiar dovada primirii.
    (!predaAcum || Boolean(semnatura))

  /*
    Ce lipseste ca sa se poata trimite, spus pe scurt sub buton.

    Un buton stins fara explicatie e cea mai frecventa cale prin care un om abandoneaza un
    formular: crede ca s-a stricat ceva. Ordinea urmeaza ordinea campurilor de pe ecran, ca sa
    stie unde sa se intoarca.
  */
  const ceLipseste =
    !nume.trim() || nume.trim().length < 2
      ? 'Completează numele.'
      : prenume.trim().length < 2
        ? 'Completează prenumele.'
        : !/.+@.+\..+/.test(email.trim())
          ? 'Scrie o adresă de e-mail validă.'
          : tombola && telefon.trim().length < 9
            ? 'Completează numărul de telefon.'
            : !acordInformare
              ? 'Bifează informarea privind prelucrarea datelor.'
              : tombola && !confirmare18
                ? 'Confirmă că ai cel puțin 18 ani.'
                : actiune?.textRegulament && !acceptRegulament
                  ? 'Bifează acceptarea regulamentului.'
                  : predaAcum && !semnatura
                    ? 'Semnează în căsuța de mai sus.'
                    : ''

  async function trimite(e: React.FormEvent) {
    e.preventDefault()
    // Plasa de siguranta pentru Enter din tastatura: inscrierea se trimite o singura data.
    if (!gata || seTrimite || nrPv) return
    setSeTrimite(true)
    setEroare(null)
    try {
      const r = await axios.post<{ nrPv: string | null; cadouToken: string | null }>(
        `/api/public/sampling/${encodeURIComponent(slug)}/predare`,
        {
          nume: nume.trim(),
          prenume: prenume.trim(),
          email: email.trim(),
          telefon: telefon.trim() || undefined,
          functie: functie || undefined,
          observatii: observatii.trim() || undefined,
          acordInformare,
          acceptRegulament,
          confirmare18,
          acordMarketingEmail: acordEmail,
          acordMarketingSms: acordSms,
          agentUserId: agent ? Number(agent) : undefined,
          semnatura: semnatura ?? undefined,
          website: website || undefined,
        },
      )
      setNrPv(r.data.nrPv ?? '—')
      /*
        Inscrierea e salvata. Daca actiunea are un cadou, urmeaza intrebarea daca il vrea —
        participarea la tombola nu depinde de raspuns. Daca nu are, se merge direct la confirmare.
      */
      setCadouToken(r.data.cadouToken ?? null)
      setPas(cadou ? 'salvata' : 'gata')
      // Ecranul de confirmare e scurt, iar formularul era lung: fara asta omul ramane cu
      // privirea sub el, pe gol, si crede ca nu s-a intamplat nimic. Exact ce a pățit un
      // participant real, care a vazut confirmarea abia cand i-a venit emailul.
      window.scrollTo({ top: 0, behavior: 'auto' })
    } catch (err) {
      setEroare(
        mesajEroare(
          err,
          tombola
            ? 'Nu am putut salva înscrierea. Te rugăm să încerci din nou.'
            : 'Nu am putut înregistra predarea. Te rugăm să încerci din nou.',
        ),
      )
    } finally {
      setSeTrimite(false)
    }
  }

  /*
    Pasul 2 — „vreau produsul gratuit".

    Marcheaza intentia pe inscriere, ca s-o vada consultantul la stand: lista de acolo arata „a
    cerut cadoul in formular" langa fiecare om. Nu preda nimic si nu poate esua vizibil — daca
    reteaua cade, omul merge mai departe la confirmare, iar la stand se intreaba ca inainte.
  */
  async function cerCadoul() {
    setVreaCadou(true)
    setPas('gata')
    window.scrollTo({ top: 0, behavior: 'auto' })
    if (!cadouToken) return
    try {
      await axios.post(`/api/public/sampling/${encodeURIComponent(slug)}/cadou-cerut`, {
        token: cadouToken,
      })
    } catch {
      // Intentia e o inlesnire pentru stand, nu o conditie. Nu se arata nicio eroare.
    }
  }

  if (eroareIncarcare) {
    return (
      <Ecran>
        <h1 className="text-xl font-semibold text-b4l-text">Formularul nu este disponibil</h1>
        <p className="mt-2 text-sm text-b4l-text-muted">{eroareIncarcare}</p>
      </Ecran>
    )
  }

  if (!actiune) {
    return (
      <Ecran>
        <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
      </Ecran>
    )
  }

  /*
    Pasul 3 — confirmarea. Ultimul ecran.

    Ce scrie pe el depinde de ce a ales la pasul 2: cine vrea produsul primeste instructiunile de
    la stand, cine nu, se opreste aici. Participarea la tombola e aceeasi in ambele cazuri.
  */
  if (pas === 'gata' && nrPv) {
    return (
      <Ecran>
        <Pasi pas={pas} onMergiLa={setPas} />
        <div className="rounded-lg bg-b4l-green-light p-4 text-center">
          <p className="text-lg font-semibold text-b4l-green-dark">
            {tombola ? 'Ești înscris(ă) la tombolă!' : 'Mulțumim!'}
          </p>
          <p className="mt-1 text-sm text-b4l-green-dark">
            Înscrierea <strong>{nrPv}</strong> a fost înregistrată.
          </p>
        </div>
        <p className="mt-4 text-sm text-b4l-text-muted">
          Confirmarea îți vine pe email, la <strong>{email.trim()}</strong>, în câteva minute. Dacă
          nu ajunge, verifică și folderul Spam.
        </p>
        {/*
          Ce urmeaza, spus pe ecranul pe care omul il are deja in mana. Fara randul asta,
          formularul se termina cu „multumim" si nimeni nu-i spune ca mostrele nu vin singure.
        */}
        {!tombola && (
          <p className="mt-3 rounded-lg border border-b4l-border bg-white p-3 text-sm text-b4l-text-muted">
            Pentru mostre, adresează-te unui consultant Boost4Life. El îți dă produsele și semnezi
            de primire pe tableta lui — atunci pleacă și procesul-verbal, pe același email.
          </p>
        )}

        {/*
          Instructiunile pentru produs, doar cui l-a cerut. Nu un buton — predarea se face la
          stand, pe telefonul consultantului, ca sa existe o singura verificare, facuta de cine
          trebuie.
        */}
        {cadou && vreaCadou && (
          <div className="mt-4 rounded-lg border border-b4l-border bg-white p-4">
            {/*
              Textul cerut de Alexandru, cuvant cu cuvant. Primul rand ramane cel configurat pe
              actiune („Conditia pentru cadou"), fiindca acelasi text apare si la pasul 2 si nu are
              voie sa spuna doua lucruri diferite in acelasi formular.
            */}
            <p className="text-sm font-medium text-b4l-text">Primește un produs gratuit</p>
            {cadou.conditie && (
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-b4l-text-muted">
                {cadou.conditie}
              </p>
            )}
            <p className="mt-2 text-sm text-b4l-text-muted">
              Primești produsul pe loc, în limita stocului disponibil. La ridicare, confirmi
              primirea prin semnătură pe telefonul consultantului.
            </p>
          </div>
        )}
        {cadou && !vreaCadou && (
          <p className="mt-4 rounded-lg border border-b4l-border bg-white p-3 text-sm text-b4l-text-muted">
            Te-ai oprit la înscriere. Dacă te răzgândești, produsul gratuit se cere la stand, de la
            un consultant Boost4Life.
          </p>
        )}
      </Ecran>
    )
  }

  /*
    Pasul 2 — inscrierea e salvata, iar omul alege daca vrea si produsul gratuit.

    „Participarea ta ramane valida indiferent daca alegi sau nu produsul gratuit" nu e politete:
    e motivul pentru care inscrierea se comite INAINTE de aceasta intrebare. Cine abandoneaza
    aici ramane inscris.

    Alegerea nu preda nimic. Marcheaza doar ca a cerut, ca sa vada consultantul la stand.
  */
  if (pas === 'salvata' && cadou) {
    return (
      <Ecran>
        <Pasi pas={pas} onMergiLa={setPas} />
        <div className="rounded-lg border border-b4l-border bg-white p-4">
          <p className="text-sm text-b4l-green-dark">✓ Înscriere salvată</p>
          <h1 className="mt-1 text-xl font-semibold text-b4l-text">Ești înscris(ă) la tombolă</h1>
          <p className="mt-1 text-sm text-b4l-text-muted">
            Participarea ta rămâne validă indiferent dacă alegi sau nu produsul gratuit.
          </p>
          {cadou.conditie && (
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-b4l-text">
              {cadou.conditie}
            </p>
          )}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button
              onClick={() => {
                setVreaCadou(false)
                setPas('gata')
                window.scrollTo({ top: 0, behavior: 'auto' })
              }}
              className="h-12 flex-1 rounded-lg border border-b4l-border bg-white text-sm font-medium text-b4l-text"
            >
              Finalizez doar înscrierea
            </button>
            <button
              onClick={cerCadoul}
              className="h-12 flex-1 rounded-lg bg-b4l-text text-sm font-semibold text-white"
            >
              Vreau produsul gratuit
            </button>
          </div>
        </div>
      </Ecran>
    )
  }

  return (
    <Ecran>
      {/* Bara sta pe TOATE ecranele, inclusiv pe primul: altfel omul nu stie ca urmeaza ceva. */}
      <Pasi pas="date" onMergiLa={nrPv ? setPas : undefined} />

      {/*
        Afisul campaniei, in capul paginii. Modelul cerut de Alexandru — campania Nestlé de la
        Mega Image — incepe cu un banner, apoi formularul. Fara el, cine scaneaza codul QR
        ajunge direct pe campuri si nu are nicio confirmare vizuala ca a nimerit unde trebuie.

        Vine de la un endpoint propriu, nu din raspunsul formularului: se cache-uieste in browser
        si nu ingreuneaza fiecare deschidere. `aspect-[2/1]` tine locul cat se incarca, altfel
        titlul sare in jos sub ochii omului.
      */}
      {actiune.areAfis && (
        <img
          src={`/api/public/sampling/${encodeURIComponent(slug)}/afis?v=${actiune.afisVersiune}`}
          alt={actiune.denumire}
          className="-mx-4 mb-4 aspect-[2/1] w-[calc(100%+2rem)] object-cover"
        />
      )}

      <h1 className="text-xl font-semibold text-b4l-text">{actiune.denumire}</h1>
      {actiune.locatie && <p className="text-sm text-b4l-text-muted">{actiune.locatie}</p>}

      {/*
        Textul de deasupra formularului. Vine de pe actiune, cand cineva l-a scris; altfel
        ramane cel implicit, ca pana acum.

        Era scris in cod, deci identic la orice tombola — desi tocmai el spune ce primeste omul
        si ce nu, iar asta difera de la o actiune la alta.
      */}
      {(actiune.textIntro || tombola) && (
        <p className="mt-4 whitespace-pre-wrap rounded-lg bg-white p-3 text-sm text-b4l-text-muted">
          {actiune.textIntro ??
            'Completează datele de mai jos pentru a te înscrie la tombolă. Prin acest formular nu se predau produse.'}
        </p>
      )}

      <form onSubmit={trimite} className="mt-6 space-y-4">
        {/* Doua campuri scurte pe un rand: economiseste o inaltime intreaga pe telefon. */}
        <div className="grid grid-cols-2 gap-3">
          <Intrare
            label="Nume"
            value={nume}
            onChange={setNume}
            autoComplete="family-name"
            required
          />
          <Intrare
            label="Prenume"
            value={prenume}
            onChange={setPrenume}
            autoComplete="given-name"
            required
          />
        </div>
        <Intrare
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          autoComplete="email"
          required
          /*
            Textul spune ce se intampla ACUM si ce urmeaza, in ordinea asta. Cat timp formularul
            preda, scria „primesti procesul-verbal semnat" — o promisiune care, de cand predarea
            se face la stand, se implinea abia peste o ora sau deloc, daca omul pleca fara mostre.
          */
          hint={
            tombola
              ? 'Pe această adresă primești confirmarea înscrierii.'
              : 'Pe această adresă primești confirmarea, iar procesul-verbal când consultantul îți dă mostrele.'
          }
        />
        <Intrare
          label={tombola ? 'Telefon' : 'Telefon (opțional)'}
          value={telefon}
          onChange={setTelefon}
          type="tel"
          autoComplete="tel"
          required={tombola}
          hint={
            tombola
              ? 'Pe acest număr te contactăm dacă ești desemnat(ă) câștigător(oare).'
              : undefined
          }
        />

        {/*
          Lista de ocupații vine de la server. Goală înseamnă „nu întreba" — la tombolă
          ocupația n-are ce căuta, iar decizia stă într-un singur loc, nu în două.
        */}
        {actiune.functii.length > 0 && (
          <label className="block">
            <span className="text-sm font-medium text-b4l-text">Ocupație (opțional)</span>
            <select
              value={functie}
              onChange={(e) => setFunctie(e.target.value)}
              className="mt-1 h-12 w-full rounded-lg border border-b4l-border bg-white px-3 text-base"
            >
              <option value="">Alege…</option>
              {actiune.functii.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
        )}

        {/*
          Observatii, optional (Madalin, 22.10.2026). La toate actiunile cu QR. Echipa le vede la
          stand, sub numele omului, si in lista de predari. Text de 16px: pe iPhone, un camp mai
          mic face zoom la atingere.
        */}
        <label className="block">
          <span className="text-sm font-medium text-b4l-text">Observații (opțional)</span>
          <textarea
            value={observatii}
            onChange={(e) => setObservatii(e.target.value)}
            maxLength={MAX_OBSERVATII}
            rows={3}
            className="mt-1 min-h-24 w-full rounded-lg border border-b4l-border bg-white px-3 py-2 text-base"
          />
          {observatii.length > MAX_OBSERVATII - 100 && (
            <span className="mt-1 block text-xs text-b4l-text-dim">
              {observatii.length} / {MAX_OBSERVATII}
            </span>
          )}
        </label>

        {/* Capcana pentru roboți: ascunsă vizual, dar completabilă de un script. */}
        <input
          type="text"
          name="website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
        />

        {/*
          Vârsta. Regulamentul oficial o cere la art. 4.1 (dreptul de participare) și o listează
          la art. 5.1 printre câmpurile obligatorii ale formularului.

          Se cere o BIFĂ, nu data nașterii: ce trebuie dovedit e că a declarat, nu câți ani are.
          O dată de naștere ar fi un câmp în plus de păzit, fără niciun folos.
        */}
        {tombola && (
          <label className="flex items-start gap-2 rounded-lg border border-b4l-border bg-white p-3">
            <input
              type="checkbox"
              checked={confirmare18}
              onChange={(e) => setConfirmare18(e.target.checked)}
              className="mt-1 h-5 w-5"
            />
            <span className="text-sm text-b4l-text">
              Confirm că am cel puțin 18 ani. <span className="text-b4l-red">*</span>
            </span>
          </label>
        )}

        {/*
          Textele juridice ca LINKURI, nu desfasurate in pagina.

          Alexandru: „vrem ca pagina de inscriere sa respecte logica de aici", aratand campania
          Nestlé de la Mega Image. Acolo regulamentul si nota de informare sunt linkuri catre
          documente separate, iar formularul ramane scurt — bife si atat.

          Erau, pana acum, casete restranse cu textul intreg in pagina. Si acelea erau valide, dar
          formularul tot avea de derulat, iar cu regulamentul adaugat crestea din nou.

          CE NU SE MUTA IN LINK: identitatea operatorului si scopul. Ghidul EDPB 05/2020, nota 42,
          le cere in PRIMUL strat — altfel nu se poate demonstra ca acordul a fost informat. De
          aceea fraza de mai jos nu se ascunde si nu se restrange niciodata.

          Linkurile se deschid in fila noua. Omul e la stand, cu telefonul in mana si formularul
          pe jumatate completat; o navigare care il scoate din pagina i-ar sterge tot.
        */}
        <div className="rounded-lg border border-b4l-border bg-white p-3">
          {/*
            NUMAI BIFE, fara paragrafele de proza.

            Victor, aratand pagina Nestle: sectiunea cu cele doua paragrafe „nu ar trebui sa mai
            existe". Are dreptate, si nu doar estetic. Declaratia lunga repeta cuvant cu cuvant ce
            spun bifele de dedesubt — iar cand acelasi angajament apare de doua ori, o data ca text
            si o data ca bifa, dovada e mai slaba, nu mai tare: nu se mai stie la care a consimtit
            omul. O bifa = un scop = un temei, si atat.

            Ce NU se poate scoate e randul de identitate. Cine prelucreaza si pentru ce trebuie sa
            fie in PRIMUL strat, nu doar in documentul din spatele linkului — art. 13 alin. (1)
            lit. (a) si (c) GDPR, si ghidul EDPB 05/2020, nota 42. La Nestle informatia asta e
            purtata de context: esti pe nestle.ro. Noi suntem pe crm.boost4life.ro, un domeniu care
            nu spune nimanui ce firma e in spate. Deci a ramas un singur rand, nu un paragraf.

            A cazut si fraza „inscrierea la tombola nu ma obliga sa postez un Story". Ea traia in
            declaratie; acum o spune, la locul ei, nota de langa produsul gratuit — acolo unde omul
            chiar se intreaba daca trebuie sa posteze ceva.
          */}
          <p className="text-sm leading-relaxed text-b4l-text-muted">
            Datele sunt prelucrate de{' '}
            <strong className="text-b4l-text">Divers Seo Market S.R.L.</strong> (Boost4Life){' '}
            {tombola
              ? 'pentru înscrierea în tombolă, contactarea câștigătorilor și predarea premiilor'
              : 'pentru evidența mostrelor primite'}
            .
          </p>

          {/*
            Cand regulamentul lipseste, se SPUNE. Pana acum sectiunea disparea in tacere — iar
            declaratia de deasupra continua sa zica „am citit si accept Regulamentul Campaniei",
            despre un document pe care nimeni nu-l putea deschide.
            Alexandru: „nu apare camp pentru vizualizare regulament".

            Aceeasi solutie ca la informare, doua blocuri mai jos: un gol legal trebuie sa se vada
            de pe ecran, nu sa fie descoperit de cine vine sa controleze.
          */}
          {tombola && !actiune.textRegulament && !actiune.urlRegulament && (
            <p className="mt-2 text-xs text-b4l-red">
              Regulamentul Campaniei nu este încărcat în aplicație. Anunță reprezentantul înainte de
              a continua.
            </p>
          )}

          {(actiune.textRegulament || actiune.urlRegulament) && (
            <label className="mt-3 flex items-start gap-2">
              <input
                type="checkbox"
                checked={acceptRegulament}
                onChange={(e) => setAcceptRegulament(e.target.checked)}
                className="mt-1 h-5 w-5"
              />
              <span className="text-sm text-b4l-text">
                Am citit și accept{' '}
                {/*
                  Linkul extern al actiunii bate pagina din CRM — acolo traieste textul autentificat
                  notarial. Alexandru: „cum avem aici pentru politica de confidentialitate, vreau sa
                  fie si pentru regulament".
                */}
                {actiune.urlRegulament ? (
                  <a
                    href={actiune.urlRegulament}
                    target="_blank"
                    rel="noreferrer"
                    className="text-b4l-blue underline"
                  >
                    Regulamentul oficial al Campaniei ↗
                  </a>
                ) : (
                  <LinkDocument slug={slug} ancora="regulament">
                    Regulamentul oficial al Campaniei
                  </LinkDocument>
                )}
                . <span className="text-b4l-red">*</span>
              </span>
            </label>
          )}

          <label className="mt-3 flex items-start gap-2">
            <input
              type="checkbox"
              checked={acordInformare}
              onChange={(e) => setAcordInformare(e.target.checked)}
              className="mt-1 h-5 w-5"
            />
            {/*
              Linkul bifei duce la POLITICA de pe site, cand actiunea are una.

              Alexandru: „politica de confidentialitate trebuie sa fie accesata de la «Am luat la
              cunostinta»". Are dreptate ca asezare — art. 3.5 din regulament spune ca politica e
              „accesibila direct din Formular", iar bifa e singurul loc din formular unde omul
              declara ca a citit ceva.

              ATENTIE la ce se schimba odata cu asta: pana acum linkul deschidea informarea
              VERSIONATA din CRM, iar `textInformareId` se scria pe inscriere ca dovada a exact
              ceea ce s-a afisat. Pagina de pe magazin nu e versionata de noi: daca se modifica
              maine, nu mai putem arata ce scria in ziua semnarii. Versiunea din CRM se retine in
              continuare, deci dovada exista — dar cele doua texte trebuie sa spuna acelasi lucru,
              altfel dovada e despre alt document decat cel citit.

              Fara politica pe actiune se revine la informarea din CRM.
            */}
            <span className="text-sm text-b4l-text">
              Am luat la cunoștință{' '}
              {actiune.urlPolitica ? (
                <a
                  href={actiune.urlPolitica}
                  target="_blank"
                  rel="noreferrer"
                  className="text-b4l-blue underline"
                >
                  Politica de confidențialitate ↗
                </a>
              ) : (
                <LinkDocument slug={slug} ancora="informare">
                  Informarea privind prelucrarea datelor
                </LinkDocument>
              )}
              . <span className="text-b4l-red">*</span>
            </span>
          </label>

          {/*
            Marketingul rămâne pe două bife, una per canal. Anexa 2 din regulament cere una
            singură, „prin e-mail/SMS" — dar un singur consimțământ pentru două canale înseamnă că
            dezabonarea de la e-mail taie și SMS-ul, sau invers. Un canal, un acord.
          */}
          <label className="mt-3 flex items-start gap-2">
            <input
              type="checkbox"
              checked={acordEmail}
              onChange={(e) => setAcordEmail(e.target.checked)}
              className="mt-1 h-5 w-5"
            />
            <span className="text-sm text-b4l-text">
              {/*
                Fara „Detalii". Alexandru: „sterge Detalii, toate datele pentru Regulament/GDPR or
                sa fie accesibile prin link-uri".

                Are dreptate ca era in plus: deasupra sunt deja doua linkuri, catre Regulament si
                catre Politica, iar al treilea intr-un rand de bifa incarca ecranul fara sa adauge.
                Substanta consimtamantului e chiar in eticheta — cine trimite, ce trimite, pe ce
                canal — iar retragerea se explica in subsolul fiecarui mesaj comercial.
              */}
              Doresc să primesc noutăți și oferte Boost4Life pe email.
            </span>
          </label>
          <label className="mt-2 flex items-start gap-2">
            <input
              type="checkbox"
              checked={acordSms}
              onChange={(e) => setAcordSms(e.target.checked)}
              disabled={!telefon.trim()}
              className="mt-1 h-5 w-5"
            />
            <span className={`text-sm ${telefon.trim() ? 'text-b4l-text' : 'text-b4l-text-muted'}`}>
              Doresc să primesc noutăți și oferte Boost4Life prin SMS.
              {!telefon.trim() && ' (completează întâi telefonul)'}
            </span>
          </label>

          {!actiune.textInformare && (
            <p className="mt-3 text-xs text-b4l-red">
              Textul informării nu este configurat. Anunță reprezentantul înainte de a continua.
            </p>
          )}
        </div>

        {/*
          Cadoul: se anunta ca exista, si SUB CE REZERVA.

          Regulamentul cere amandoua: art. 9.5 — mentiunea „in limita stocului disponibil" sa fie
          vizibila si in Formular; art. 9.6 — epuizarea sa fie anuntata „si in Formular cat mai
          curand posibil". Cat timp nu stim stocul nu afirmam nimic: o retea lenta n-are voie sa
          spuna „s-a terminat" despre marfa care exista.
        */}
        {cadou && (
          <div className="rounded-lg border border-b4l-border bg-white p-3 text-sm">
            {stocEpuizat === true ? (
              <p className="text-b4l-red-dark">
                <strong>Stocul de produse gratuite s-a epuizat.</strong> Înscrierea la tombolă merge
                mai departe, neîngrădită.
              </p>
            ) : (
              /*
                CONDITIA, spusa aici — nu abia la pasul 2.

                Alexandru: „textul (...) nu precizeaza nimic despre postare. Informatia asta apare
                in pasul 2 la Inscriere salvata". Adica omul afla ce are de facut pentru produs
                DUPA ce s-a inscris. Victor ceruse exact pe dos: „chiar ii aducem la cunostinta ca
                trebuie sa faca acel lucru ca sa poata bifa".

                Textul e cel scris pe actiune (`textCadou`), acelasi care apare la pasul 2 — deci
                nu se pot contrazice. Punctul final se pune aici: in camp se scrie o propozitie,
                nu o fraza cu punctuatie de final.
              */
              <p className="text-b4l-text-muted">
                {cadou.conditie ? (
                  <>{cadou.conditie.replace(/\s*\.\s*$/, '')}. </>
                ) : (
                  <>
                    După înscriere poți cere și un <strong>produs gratuit</strong>, în limita
                    stocului disponibil.{' '}
                  </>
                )}
                Nu îți mărește șansele la tombolă.
              </p>
            )}
          </div>
        )}

        {eroare && (
          <p className="rounded-lg bg-b4l-red-light p-3 text-sm text-b4l-red-dark">{eroare}</p>
        )}

        {/*
          BARA DE TRIMITERE, lipita jos.

          Raspunsul direct la „sa nu fie nevoie sa deruleze atat de mult pentru confirmare":
          butonul urmareste degetul, in loc sa stea la capatul derularii. Nu se mai cauta.

          Cand e stins, randul de sub el spune CE lipseste. Un buton stins fara explicatie e cea
          mai frecventa cale prin care un om abandoneaza un formular — crede ca s-a stricat ceva.

          `pb-[env(safe-area-inset-bottom)]` tine bara deasupra barei de gesturi de pe iPhone.
        */}
        <div className="sticky bottom-0 -mx-4 mt-2 border-t border-b4l-border bg-b4l-bg px-4 pb-[env(safe-area-inset-bottom)] pt-3">
          {nrPv ? (
            <>
              <p className="mb-2 rounded-lg bg-b4l-blue-light p-3 text-sm text-b4l-blue-dark">
                Înscrierea <strong>{nrPv}</strong> e deja salvată. Datele de mai sus sunt cele
                trimise. Dacă ai greșit ceva, spune-i unui consultant la stand.
              </p>
              <button
                type="button"
                onClick={() => {
                  setPas(cadou ? 'salvata' : 'gata')
                  window.scrollTo({ top: 0, behavior: 'auto' })
                }}
                className="h-14 w-full rounded-lg bg-b4l-blue text-base font-semibold text-white"
              >
                Înapoi la confirmare
              </button>
            </>
          ) : (
            <>
              {/*
                SEMNĂTURA, la acțiunile care predau pe loc.

                Stă imediat deasupra butonului, cu lista a ce primește scrisă lângă ea: omul
                semnează și de primire, deci trebuie să vadă pentru CE semnează, nu doar unde.

                Există pentru evenimentele mici, unde datele se strâng și produsele se dau în
                aceeași clipă. Fără ea, al doilea pas înseamnă oprirea omului a doua oară — iar
                când el a plecat deja, procesul-verbal rămâne nesemnat. La un eveniment real
                agentul a semnat el, la final, în numele participanților plecați.
              */}
              {predaAcum && (
                <div className="rounded-lg border border-b4l-border p-3">
                  <p className="text-sm font-medium text-b4l-text">
                    Semnează de primirea produselor
                  </p>
                  {actiune && actiune.produse.length > 0 && (
                    <ul className="mt-1 list-disc pl-5 text-sm text-b4l-text-muted">
                      {actiune.produse.map((p, i) => (
                        <li key={i}>
                          {p.denumire} — {p.cantitate} {p.um}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-2 text-xs text-b4l-text-muted">
                    Semnătura de mai jos confirmă și acordul pentru prelucrarea datelor, și primirea
                    produselor de mai sus. Procesul-verbal îți ajunge pe email.
                  </p>
                  <div className="mt-2">
                    <SemnaturaPad onChange={setSemnatura} disabled={seTrimite} />
                  </div>
                </div>
              )}
              <button
                type="submit"
                disabled={!gata || seTrimite}
                className="h-14 w-full rounded-lg bg-b4l-blue text-base font-semibold text-white disabled:opacity-40"
              >
                {/*
                  Butonul spune ce se intampla CHIAR ACUM.

                  A scris „Confirm primirea mostrelor" cat timp formularul preda. Nu mai preda —
                  omul isi trimite datele, si atat. Un buton care confirma primirea unor mostre pe
                  care nu le are inca in mana e exact genul de text pe care nimeni nu-l citeste
                  pana nu e prea tarziu.
                */}
                {seTrimite
                  ? 'Se trimite…'
                  : tombola
                    ? // Numele din Anexa 2 a regulamentului. Declaratia de deasupra il citeaza.
                      'Înscrie-mă'
                    : predaAcum
                      ? // Aici chiar se preda: butonul spune ce face, nu ce facea inainte.
                        'Confirm primirea produselor'
                      : 'Trimite datele'}
              </button>
              {!gata && !seTrimite && (
                <p className="mt-1.5 text-center text-xs text-b4l-text-muted">{ceLipseste}</p>
              )}
            </>
          )}
        </div>
      </form>

      {/*
        Acordeoanele din josul paginii, ca in macheta Nestlé aratata de Alexandru: sub butonul de
        trimitere, „Regulamentul campaniei" si „Câștigători", inchise, care se desfac la clic.

        Nu dubleaza linkurile din dreptul bifelor — le completeaza, si asa e si la ei. Linkul de
        langa bifa e pentru cine vrea sa citeasca INAINTE sa bifeze; acordeonul e pentru cine
        deruleaza pagina si se intreaba „bine, si care sunt regulile?". Doua momente diferite.

        Castigatorii se cer de la server abia la deschidere: pana la extragere nu e nimic acolo,
        iar o cerere in plus la fiecare scanare de cod QR e o cerere degeaba.
      */}
      <div className="mt-6 border-t border-b4l-border">
        {actiune.textRegulament && (
          <Acordeon titlu="Regulamentul campaniei">
            {/*
              O fraza si un buton, nu contractul intreg.

              Victor, comparand cot la cot cu pagina Nestlé: acolo acordeonul spune „consulta
              regulamentul de mai jos pentru alte detalii" si da un buton. La noi revarsa tot
              textul in pagina — un regulament notarial cu 15 sectiuni si 4 anexe, pe un telefon,
              intre butonul de trimitere si subsol.

              Se potriveste si cu decizia de a tine textele juridice ca linkuri: acelasi principiu,
              acelasi loc unde duc.
            */}
            <p className="text-sm text-b4l-text-muted">
              Consultă regulamentul complet al campaniei pentru toate detaliile: cine poate
              participa, ce premii sunt, cum se face extragerea.
            </p>
            {/*
              Cand actiunea are link extern, butonul duce acolo: pe site traieste textul
              autentificat notarial, iar pagina din CRM ar arata o copie care se poate despari de
              original la prima corectura.
            */}
            <a
              href={actiune.urlRegulament ?? `/s/${encodeURIComponent(slug)}/reguli#regulament`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-block rounded-lg bg-b4l-blue px-4 py-2 text-sm font-medium text-white"
            >
              Vezi regulamentul ↗
            </a>
          </Acordeon>
        )}
        {/*
          Doar la tombola. Pe o actiune de sampling nu se extrage nimeni, deci „Castigatori" era
          o intrebare fara raspuns, pusa fix sub butonul de trimitere (Alexandru, 22.09.2026).
        */}
        {tombola && (
          <Acordeon titlu="Câștigători">
            <Castigatori slug={slug} />
          </Acordeon>
        )}
      </div>
    </Ecran>
  )
}

/**
 * Link către un document al campaniei, pe pagina publică de reguli.
 *
 * Fila nouă, mereu. Omul e la stand, cu telefonul în mână și formularul pe jumătate completat;
 * o navigare care îl scoate din pagină i-ar șterge tot ce a scris. Săgeata de la final o spune
 * dinainte, ca deschiderea unei file noi să nu fie o surpriză.
 */
/**
 * Secțiune pliabilă, în josul paginii. Titlu pe un rând, plus sau minus la capăt.
 *
 * Închisă la început, mereu: pagina e a formularului, nu a regulamentului. Cine vine să se
 * înscrie nu trebuie să deruleze printr-un contract ca să ajungă la buton — iar butonul stă
 * oricum lipit jos, deasupra ei.
 */
function Acordeon({ titlu, children }: { titlu: string; children: React.ReactNode }) {
  const [deschis, setDeschis] = useState(false)
  return (
    <div className="border-b border-b4l-border">
      <button
        type="button"
        onClick={() => setDeschis((v) => !v)}
        aria-expanded={deschis}
        className="flex w-full items-center justify-between py-4 text-left"
      >
        <span className="text-base font-medium text-b4l-text">{titlu}</span>
        <span className="text-xl leading-none text-b4l-blue">{deschis ? '−' : '+'}</span>
      </button>
      {deschis && <div className="pb-4">{children}</div>}
    </div>
  )
}

interface CastigatorPublic {
  id: number
  nrExtras: number
  nume: string
  extrasLa: string
  /** Cum s-a stabilit numarul: random.org, sau „un singur inscris" cand n-a fost ce extrage. */
  sursa: string
  areDovada: boolean
  /** Înregistrarea extragerii la random.org, când a trecut prin Draw Service. */
  recordUrl: string | null
  /** Premiul extras dar neridicat în termen: s-a reportat, potrivit art. 6.3 și 4.8. */
  neridicat: boolean
}

/**
 * Duce dovada la formularul public de verificare al celor de la random.org.
 *
 * Alexandru: „pai atunci cum poate un castigator sa verifice?". Asa. Raspunsul semnat pleaca prin
 * POST catre ei, iar ei confirma ca numarul a iesit acolo, la momentul acela, pentru intervalul
 * acela. Noi nu-l putem falsifica: semnatura e a lor, nu a noastra.
 *
 * Prin formular, nu prin link: verificarea lor primeste datele prin POST, iar semnatura e prea
 * lunga pentru un URL.
 */
async function verificaPublic(slug: string, id: number) {
  const d = (
    await axios.get<{ random: unknown; signature: string }>(
      `/api/public/sampling/${encodeURIComponent(slug)}/extrageri/${id}/dovada`,
    )
  ).data
  const f = document.createElement('form')
  f.method = 'POST'
  f.action = 'https://api.random.org/signatures/form'
  f.target = '_blank'
  f.rel = 'noreferrer'
  for (const [nume, valoare] of [
    ['format', 'json'],
    ['random', JSON.stringify(d.random)],
    ['signature', d.signature],
  ]) {
    const i = document.createElement('input')
    i.type = 'hidden'
    i.name = nume
    i.value = valoare
    f.appendChild(i)
  }
  document.body.appendChild(f)
  f.submit()
  f.remove()
}

/**
 * Lista câștigătorilor, în acordeon.
 *
 * Se cere la deschidere, nu la încărcarea paginii: până la extragere nu e nimic acolo, iar o
 * cerere în plus la fiecare scanare de cod QR e o cerere degeaba.
 *
 * Publicarea nu e o alegere de comunicare — OG 99/2000 art. 42 alin. (2) o cere, iar
 * regulamentul o prevede la art. 8.5. Se afișează puțin: numărul extras, prenumele și inițiala
 * numelui, „atât cât este necesar", cum scrie chiar acolo.
 */
function Castigatori({ slug }: { slug: string }) {
  const [lista, setLista] = useState<CastigatorPublic[] | null>(null)
  const [eroare, setEroare] = useState(false)

  useEffect(() => {
    let anulat = false
    axios
      .get<{ castigatori: CastigatorPublic[] }>(
        `/api/public/sampling/${encodeURIComponent(slug)}/castigatori`,
      )
      .then((r) => {
        if (!anulat) setLista(r.data.castigatori)
      })
      .catch(() => {
        if (!anulat) setEroare(true)
      })
    return () => {
      anulat = true
    }
  }, [slug])

  if (eroare) {
    return <p className="text-sm text-b4l-text-muted">Nu am putut încărca lista acum.</p>
  }
  if (!lista) return <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
  if (lista.length === 0) {
    /*
      Inainte de extragere. Formularea conteaza: „nu s-a facut inca" linisteste, „nu exista
      castigatori" sperie pe cine a participat si verifica.
    */
    return (
      <>
        <p className="text-sm text-b4l-text-muted">
          Extragerea nu a avut loc încă. Câștigătorii vor fi publicați aici, conform regulamentului
          campaniei.
        </p>
        {/*
          Butonul catre pagina dedicata, ca la Nestlé. Cine se intoarce peste o saptamana sa vada
          daca a castigat n-o sa mai aiba formularul deschis — are nevoie de o adresa proprie, si
          aceea e chiar cea scrisa in regulament la art. 8.5.
        */}
        <a
          href={`/s/${encodeURIComponent(slug)}/castigatori`}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-block rounded-lg bg-b4l-blue px-4 py-2 text-sm font-medium text-white"
        >
          Pagina câștigătorilor ↗
        </a>
      </>
    )
  }
  return (
    <>
      <div className="overflow-hidden rounded-lg bg-white">
        {lista.map((c) => (
          <div
            key={`${c.nrExtras}-${c.extrasLa}`}
            className="flex items-center gap-3 border-b border-b4l-border px-3 py-2 last:border-0"
          >
            <span className="w-10 shrink-0 font-semibold tabular-nums text-b4l-text">
              {c.nrExtras}
            </span>
            <span className="flex-1 text-sm text-b4l-text">{c.nume}</span>
            {/*
            Verificarea, la indemana oricui — nu doar a organizatorului. Cand baza a avut un singur
            inscris nu s-a extras nimic, deci nu exista ce verifica: se spune, in loc sa se ofere un
            link care n-ar duce nicaieri.
          */}
            {c.neridicat && (
              <span
                className="shrink-0 rounded-full bg-b4l-amber-light px-2 py-0.5 text-xs text-b4l-amber-dark"
                title="Premiul nu a fost ridicat în termenul din regulament și a fost reportat"
              >
                premiu reportat
              </span>
            )}
            {c.recordUrl ? (
              <a
                href={c.recordUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-xs text-b4l-blue underline"
                title="Înregistrarea extragerii la random.org. Verifici cu numărul înscrierii tale."
              >
                verifică la random.org ↗
              </a>
            ) : c.areDovada ? (
              <button
                onClick={() => void verificaPublic(slug, c.id).catch(() => {})}
                className="shrink-0 text-xs text-b4l-blue underline"
                title="Deschide verificarea la random.org, cu semnătura acestei extrageri"
              >
                verifică ↗
              </button>
            ) : (
              <span className="shrink-0 text-xs text-b4l-text-dim">{c.sursa}</span>
            )}
          </div>
        ))}
      </div>
      {/*
      Cum se verifica, in doua randuri. Pagina de la random.org cere un „entrant identifier" si nu
      spune de unde sa-l ia; la noi acela e numarul inscrierii, din emailul de confirmare. Fara
      randul asta, omul ajunge pe o pagina in engleza cu un camp gol.
    */}
      {/*
        Ce inseamna „premiu reportat", si pe formular. Alexandru: „vreau sa adaugam si pe pagina de
        inregistrare accesibila clientilor, informatia referitoare la premiul neridicat".

        Pagina castigatorilor o avea deja, dar acolo ajunge cine o cauta anume. Formularul il vede
        toata lumea care scaneaza codul QR — iar o eticheta portocalie langa un nume, fara nicio
        lamurire, ridica exact intrebarea la care nu raspunde nimeni la stand.
      */}
      {lista.some((c) => c.neridicat) && (
        <p className="mt-2 rounded-lg bg-b4l-amber-light p-3 text-xs text-b4l-amber-dark">
          <strong>Premiu reportat</strong> înseamnă că numărul a fost extras corect, dar
          câștigătorul nu a putut fi validat sau nu s-a prezentat să ridice premiul în termenul din
          regulament. Premiul nu se pierde: se acordă printr-o extragere nouă, dintre înscrierile de
          după (art. 4.7 și 6.3).
        </p>
      )}
      {lista.some((c) => c.recordUrl) && (
        <p className="mt-2 px-1 text-xs text-b4l-text-muted">
          Apasă <strong>verifică la random.org</strong>, treci de casetă și scrie numărul înscrierii
          tale — cel din e-mailul de confirmare, în forma <code>TB-2026-000014</code>. Ei îți spun
          dacă ai fost în extragere și dacă ai fost ales.
        </p>
      )}
    </>
  )
}

function LinkDocument({
  slug,
  ancora,
  children,
}: {
  slug: string
  ancora: 'regulament' | 'informare' | 'marketing'
  children: React.ReactNode
}) {
  return (
    <a
      href={`/s/${encodeURIComponent(slug)}/reguli#${ancora}`}
      target="_blank"
      rel="noreferrer"
      className="text-b4l-blue underline"
    >
      {children} ↗
    </a>
  )
}

function Ecran({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-b4l-bg px-4 py-6">
      <div className="mx-auto max-w-md">{children}</div>
    </div>
  )
}

interface IntrareProps {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  autoComplete?: string
  required?: boolean
  hint?: string
}

/** Câmpurile sunt înalte de 48px: se nimeresc cu degetul, pe telefon, din prima. */
function Intrare({ label, value, onChange, type, autoComplete, required, hint }: IntrareProps) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-b4l-text">
        {label} {required && <span className="text-b4l-red">*</span>}
      </span>
      <input
        type={type ?? 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="mt-1 h-12 w-full rounded-lg border border-b4l-border bg-white px-3 text-base"
      />
      {hint && <span className="mt-1 block text-xs text-b4l-text-muted">{hint}</span>}
    </label>
  )
}

function mesajEroare(err: unknown, implicit: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { error?: { message?: string }; message?: string }
      | undefined
    return data?.error?.message ?? data?.message ?? implicit
  }
  return implicit
}

/**
 * Bara pașilor. Trei.
 *
 * Erau patru: gratuitatea și procesul-verbal se făceau tot aici, pe telefonul participantului.
 * Predarea s-a mutat la stand, dar alegerea „vreau produsul gratuit" a rămas — e o intenție,
 * nu o predare, și o vede consultantul în lista lui.
 *
 * E și navigabilă: pasul 1 se redeschide cu un clic din pasul 2 — „să mă pot întoarce de la
 * pasul 2 la pasul 1 cu un clic, fără să pierd datele". Datele stau în starea componentei, deci
 * nu se pierde nimic, și se poate reveni la confirmare la fel de ușor.
 *
 * Se merge doar unde s-a ajuns deja. Înainte, nu.
 */
function Pasi({
  pas,
  onMergiLa,
}: {
  pas: 'date' | 'salvata' | 'gata'
  /** Lipsește cât timp nu s-a trimis nimic: n-ai unde să te întorci de la primul pas. */
  onMergiLa?: (p: 'date' | 'salvata' | 'gata') => void
}) {
  const etape: { cheie: 'date' | 'salvata' | 'gata'; text: string }[] = [
    { cheie: 'date', text: 'Date de contact' },
    { cheie: 'salvata', text: 'Înscriere salvată' },
    { cheie: 'gata', text: 'Confirmare' },
  ]
  const ajuns = { date: 0, salvata: 1, gata: 2 }[pas]
  return (
    /*
      Ramane sus la derulare. Formularul e lung — informarea singura ocupa un ecran si jumatate —
      iar bara isi pierde rostul daca dispare exact cand omul deruleaza prin el si se intreaba
      cat mai are.

      `-mx-4 px-4` intinde fundalul peste marginile paginii, altfel textul dedesubt s-ar vedea
      prin colturi cand trece pe sub ea.
    */
    <div className="sticky top-0 z-10 -mx-4 mb-4 grid grid-cols-3 gap-1.5 bg-b4l-bg px-4 pb-2 pt-3">
      {etape.map((e, i) => {
        const sePoate = Boolean(onMergiLa) && i <= ajuns
        return (
          <button
            key={e.cheie}
            type="button"
            disabled={!sePoate}
            onClick={() => {
              if (!sePoate) return
              onMergiLa?.(e.cheie)
              window.scrollTo({ top: 0, behavior: 'auto' })
            }}
            className={`text-left ${sePoate && i !== ajuns ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className={`h-1 rounded ${i <= ajuns ? 'bg-b4l-blue' : 'bg-b4l-border'}`} />
            <div
              className={`mt-1 text-[11px] leading-tight ${
                i <= ajuns ? 'text-b4l-text' : 'text-b4l-text-dim'
              } ${sePoate && i !== ajuns ? 'underline decoration-dotted underline-offset-2' : ''}`}
            >
              {i + 1}. {e.text}
            </div>
          </button>
        )
      })}
    </div>
  )
}
